// Cart line stock-visibility resolver (DEV-50924).
//
// Reduces the already-merged cart line (see Cart.tsx `cartWithStock`) into a
// small view model — a pill category + a formatted date — so the engineer can
// spot at a glance which items are on branch stock, which are coming from a
// depot / backorder, and which are still resolving. All fields consumed here
// are produced by the /api/suppliers/cart-stock/ merge; if any are absent
// (older responses, transitional state) the resolver returns null and the
// caller renders nothing.

import type { CartItem } from '../types'
import { formatEarliestDate } from './datetime'
import { basketLineDate } from './fulfillmentDates'

export type CartItemStockPill = 'in_stock' | 'backorder' | 'checking'

export interface CartItemStockView {
  pill: CartItemStockPill
  dateLabel: string | null
}

export function getCartItemStockView(
  item: CartItem,
  fulfillment: 'pickup' | 'delivery',
  isCheckingStock: boolean,
  availableStock: number,
  quantity: number,
): CartItemStockView | null {
  // DEV-51011: every date on the line follows the order-date rule for
  // suppliers that take a date, so it never reads a closed day under a
  // group header that has moved it to Monday (AC12).
  const supportsPreferredDate = item.preferredDate?.supported === true
  const lineDateLabel = (value: string | null | undefined) =>
    formatEarliestDate(basketLineDate(value, supportsPreferredDate))

  // The dedicated STOCK UNAVAILABLE badge already covers this — don't
  // duplicate on a separate row.
  if (item.stockUnavailable) return null

  // A live probe is in flight. Show the pulsing placeholder so the date
  // doesn't flash a stale value while the qty change re-resolves.
  if (isCheckingStock) {
    return { pill: 'checking', dateLabel: null }
  }

  // The selected channel is closed for this line. The existing
  // "Not available for pickup/delivery" warning row handles it — a second
  // pill would just duplicate the message.
  const channelClosed =
    (fulfillment === 'pickup' && item.canCollect === false) ||
    (fulfillment === 'delivery' && item.canDeliver === false)
  if (channelClosed) return null

  // Backorder resolution mirrors Cart.tsx's per-channel isPickupBackorder /
  // isDeliveryBackorder rule: qty above capacity, backorder allowed for the
  // supplier, and a backorder date present for the channel.
  const backorderAllowed = item.backorderAllowed === true
  const backorderDate =
    fulfillment === 'pickup'
      ? item.backorderCollectionDate ?? null
      : item.backorderDeliveryDate ?? null
  const isBackorder =
    backorderAllowed && backorderDate != null && quantity > availableStock
  if (isBackorder) {
    return { pill: 'backorder', dateLabel: lineDateLabel(backorderDate) }
  }

  // Qty exceeds available stock and backorder can't cover it — the existing
  // red "Exceeds stock" warning row already signals this.
  if (quantity > availableStock) return null

  // In-stock split: the primary tier alone can't fulfil the requested qty but
  // the available stock covers it via a DC-later tier. Show the split inline
  // in the same row: "4 Today · 8 Tomorrow".
  //
  // Primary-tier resolution is per-channel and mirrors the tiers used in
  // SupplierProductDetail:
  //   pickup   → branch_stock + branch_earliest_collection.
  //              Pickup is always at the selected branch, so the branch
  //              shelf IS the primary tier.
  //   delivery → delivery_stock + earliest_delivery via the delivery
  //              fulfillment option.
  //              The adapter may borrow delivery from another branch, in
  //              which case delivery_stock reflects THAT source's shelf —
  //              NOT branch_stock. Using branch_stock here would falsely
  //              claim a split whenever a distinct DC-later date exists on
  //              a borrowed delivery line (P1 bug from the earlier revision).
  let primaryQty: number
  let primaryDate: string | null
  let laterDate: string | null
  if (fulfillment === 'pickup') {
    primaryQty = item.branchStock ?? 0
    primaryDate = item.branchEarliestCollection ?? null
    laterDate = item.pickupDcDate ?? null
  } else {
    const deliveryOption = item.fulfillmentOptions?.find(
      (option) => option.type === 'delivery',
    )
    primaryQty = deliveryOption?.stockQuantity ?? 0
    primaryDate =
      deliveryOption?.earliestDate ?? item.branchEarliestDelivery ?? null
    laterDate = item.deliveryDcDate ?? null
  }
  if (
    primaryQty > 0 &&
    primaryQty < quantity &&
    primaryDate != null &&
    laterDate != null
  ) {
    const primaryLabel = lineDateLabel(primaryDate)
    const laterLabel = lineDateLabel(laterDate)
    if (primaryLabel && laterLabel && primaryLabel !== laterLabel) {
      const laterQty = quantity - primaryQty
      return {
        pill: 'in_stock',
        dateLabel: `${primaryQty} ${primaryLabel} · ${laterQty} ${laterLabel}`,
      }
    }
  }

  // Non-split fallback: prefer the fully-resolved per-channel order date
  // (branch → OOD → DC precedence baked in by the merge step in Cart.tsx).
  // The channel's earliest known date is the fallback when the order date is
  // absent.
  const inStockDate =
    fulfillment === 'pickup'
      ? item.pickupOrderDate ??
        item.branchEarliestCollection ??
        item.oodEarliestCollection ??
        null
      : item.deliveryOrderDate ??
        item.branchEarliestDelivery ??
        item.oodEarliestDelivery ??
        null

  return { pill: 'in_stock', dateLabel: lineDateLabel(inStockDate) }
}
